import { VirtualEventStatus, SubmissionStatus, Frequency, SubmissionMaterializationType } from "generated-graphql/graphql";
import { partition } from "lodash-es";
import type { InputValues } from "components/Omnisearch/FilterPopover";
import { Row } from "./columns";

export interface PartitionedSubmissions {
  realSubmissions: Row[];
  virtualEvents: Row[];
  uniqueSubmissionIds: string[];
  virtualEventIds: string[];
}

/**
 * Partitions submissions into materialized submissions and virtual events based on task frequency.
 *
 * Detection logic (same as customer-facing waste submissions):
 * - Materialized instances: Task frequency === ONCE (created by materializing a virtual event)
 *   These are real submissions that were materialized from a virtual event through
 *   the proper create flow. They have their own Task (ONCE) + Submission record.
 *
 * - Virtual events: Task frequency !== ONCE (YEARLY, MONTHLY, etc.)
 *   These are computed occurrences from recurring tasks. All virtual events from
 *   the same recurring task share a template Submission record (NOT_STARTED status).
 *   They need to be materialized before they can be worked on.
 *
 * IMPORTANT: We use taskFrequency to distinguish, NOT virtualEventStatus.
 * Template submissions have recurring tasks (YEARLY, MONTHLY, etc.) and their submissionId
 * points to the template. Deleting via submissionId would delete the entire series!
 * Only ONCE tasks are truly materialized instances safe to delete/modify individually.
 *
 * NOTE: We cannot rely on submission.id existing to detect virtual events because
 * template submissions are created upfront by the create-submission-tasks lambda.
 */
export function partitionSubmissions(
  submissions: Row[]
): PartitionedSubmissions {
  const [realSubmissions, virtualEvents] = partition(
    submissions,
    (s) => s.taskFrequency === Frequency.Once
  );

  const uniqueSubmissionIds = [
    ...new Set(
      realSubmissions
        .map((s) => s.submissionId)
        .filter((id): id is string => Boolean(id))
    ),
  ];

  const virtualEventIds = virtualEvents.map((s) => s.id);

  return {
    realSubmissions,
    virtualEvents,
    uniqueSubmissionIds,
    virtualEventIds,
  };
}

/**
 * Formats a virtual event for display in a data grid
 */
export function formatVirtualEventForGrid(virtualEvent: Row) {
  return {
    id: virtualEvent.id,
    isVirtual: true,
    submission: {
      dueDate: virtualEvent.deadline,
      title: virtualEvent.title,
      submissionType: virtualEvent.submissionType,
      status: null,
    },
    facilities: virtualEvent.facilities,
  };
}

/**
 * Check if the row is a truly materialized submission (vs a virtual event or template).
 *
 * A row is truly materialized only if it has a taskFrequency of ONCE
 * (created by materializing a virtual event).
 *
 * Important: Template submissions for recurring series have recurring task frequencies
 * (YEARLY, MONTHLY, etc.) and should NOT be considered materialized even if their
 * status was changed from NOT_STARTED.
 * 
 * @deprecated Use getMaterializationType for more precise classification
 */
export function isMaterialized(row: Row): boolean {
  return row.taskFrequency === Frequency.Once;
}

/**
 * Get the materialization type of a row. This distinguishes between:
 * - VIRTUAL_EVENT: From a recurring series, not yet materialized
 * - MATERIALIZED: ONCE task that was materialized from a recurring series (has recurring parent)
 * - ONE_OFF: ONCE task created directly with no recurring parent
 * 
 * The materializationType field is computed by the backend based on whether the
 * series has a recurring task.
 */
export function getMaterializationType(row: Row): SubmissionMaterializationType {
  return row.materializationType;
}

/**
 * Check if a materialized submission can be reset to a virtual event.
 * Only submissions with ONCE tasks that were materialized from recurring series can be truly reset.
 * One-off submissions can be "reset" (cleared) but not returned to a virtual event.
 */
export function isResettable(row: Row): boolean {
  // Both MATERIALIZED and ONE_OFF can be reset (though behavior differs)
  return (
    row.materializationType === SubmissionMaterializationType.Materialized ||
    row.materializationType === SubmissionMaterializationType.OneOff
  ) && row.submissionId != null;
}

/**
 * Check if a row represents a materialized instance that can be reverted to a virtual event.
 * Only truly materialized instances (with a recurring parent) can be deleted and returned to virtual state.
 */
export function canRevertToVirtualEvent(row: Row): boolean {
  return row.materializationType === SubmissionMaterializationType.Materialized && row.submissionId != null;
}

type OmnisearchParser = (omnisearch: string) => InputValues;
type OmnisearchStringifier = (inputValues: InputValues) => string;

/**
 * Merge default key:value filters into the current omnisearch string while preserving
 * any raw free-text tokens in the user's search.
 */
export function mergeOmnisearchWithDefaultsPreservingFreeText({
  omnisearch,
  defaultSearch,
  parseOmnisearch,
  stringifyOmnisearch,
}: {
  omnisearch: string | null | undefined;
  defaultSearch: string | null | undefined;
  parseOmnisearch: OmnisearchParser;
  stringifyOmnisearch: OmnisearchStringifier;
}): string | null {
  const trimmedOmnisearch = omnisearch?.trim() ?? "";
  const trimmedDefaultSearch = defaultSearch?.trim() ?? "";

  if (!trimmedOmnisearch) {
    return trimmedDefaultSearch || null;
  }

  if (!trimmedDefaultSearch) {
    return trimmedOmnisearch;
  }

  const userFilters = parseOmnisearch(trimmedOmnisearch);
  const defaultFilters = parseOmnisearch(trimmedDefaultSearch);

  const missingDefaultFilters: InputValues = {};
  for (const [key, value] of Object.entries(defaultFilters)) {
    if (!(key in userFilters)) {
      missingDefaultFilters[key] = value;
    }
  }

  const missingDefaultsSearch = stringifyOmnisearch(missingDefaultFilters).trim();
  if (!missingDefaultsSearch) {
    return trimmedOmnisearch;
  }

  return `${trimmedOmnisearch} ${missingDefaultsSearch}`.trim();
}
